import {
  PERMISSION_CATALOG,
  ALL_PERMISSION_KEYS,
  USER_ROLES,
  normalizeRole,
  getRoleDefaultPermissions,
  resolveEffectivePermissions,
  hasPermission,
  isAdminRole,
  assignableRolesForActor,
  canManageTargetUser,
} from './permissionsData'

export type UserRole =
  | 'admin'
  | 'office'
  | 'field_manager'
  | 'crew_leader'
  | 'crew_member'
  | 'qc_inspector'
  | 'customer'

export interface PermissionOverride {
  permission_key: string
  granted: boolean
}

export interface UserProfile {
  id: string
  email: string
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  role: UserRole | string
  avatar_url?: string | null
  is_active: boolean
  primary_division_id?: string | null
  customer_id?: string | null
  tenant_id?: string | null
  is_super_admin?: boolean
  can_create_super_admins?: boolean
  /** UI language: en | es. Null = fall back to device cache / English. */
  preferred_locale?: string | null
  created_at?: string
  updated_at?: string
}

export interface TenantInfo {
  id: string
  name: string
  slug: string
  status: string
}

export {
  PERMISSION_CATALOG,
  ALL_PERMISSION_KEYS,
  USER_ROLES,
  normalizeRole,
  getRoleDefaultPermissions,
  resolveEffectivePermissions,
  hasPermission,
  isAdminRole,
  assignableRolesForActor,
  canManageTargetUser,
}

/** Route path prefix → required permission(s). */
export const ROUTE_PERMISSIONS: Array<{ prefix: string; permission: string | string[] }> = [
  { prefix: '/platform', permission: 'platform.tenants' },
  { prefix: '/dashboard', permission: 'dashboard.view' },
  { prefix: '/leads', permission: 'leads.view' },
  { prefix: '/proposals', permission: 'proposals.view' },
  { prefix: '/proposal-requests', permission: 'proposals.view' },
  { prefix: '/contracts', permission: 'contracts.view' },
  // More specific /jobs/* routes before the catalog prefix
  { prefix: '/jobs/create', permission: 'jobs.create' },
  { prefix: '/jobs/edit', permission: 'jobs.edit' },
  { prefix: '/jobs', permission: 'jobs.view' },
  { prefix: '/recurring-jobs', permission: 'jobs.view' },
  { prefix: '/job-calendar', permission: ['jobs.view', 'jobs.dispatch'] },
  { prefix: '/dispatch', permission: ['jobs.view', 'jobs.dispatch'] },
  { prefix: '/time-clock', permission: 'time.clock' },
  { prefix: '/my-day', permission: 'time.clock' },
  { prefix: '/time-review', permission: ['time.review', 'time.review_crew'] },
  { prefix: '/qc-visits', permission: 'qc.view' },
  { prefix: '/qc-schedule', permission: 'qc.schedule' },
  { prefix: '/review', permission: 'review.view' },
  { prefix: '/end-of-day', permission: 'review.view' },
  { prefix: '/billing', permission: 'financial.billing' },
  { prefix: '/billing/ready', permission: 'financial.billing' },
  { prefix: '/payments', permission: 'financial.payments' },
  { prefix: '/invoice-generation', permission: 'financial.invoices' },
  { prefix: '/pnl-dashboard', permission: 'financial.pnl' },
  { prefix: '/quickbooks-sync', permission: 'financial.quickbooks' },
  { prefix: '/reports', permission: 'reports.view' },
  { prefix: '/customers', permission: 'customers.view' },
  { prefix: '/my-properties', permission: 'properties.view' },
  { prefix: '/properties', permission: 'properties.view' },
  { prefix: '/profile', permission: 'dashboard.view' },
  { prefix: '/messages', permission: ['communication.inbox', 'communication.chat'] },
  { prefix: '/inbox', permission: 'communication.inbox' },
  { prefix: '/team-chat', permission: 'communication.chat' },
  { prefix: '/expense-review', permission: 'expenses.review' },
  { prefix: '/expenses', permission: ['expenses.submit', 'expenses.review'] },
  { prefix: '/client-portal', permission: 'client_portal.view' },
  { prefix: '/service-catalog', permission: 'settings.service_catalog' },
  { prefix: '/combination-rules', permission: 'settings.combination_rules' },
  { prefix: '/visit-group-templates', permission: 'settings.visit_templates' },
  { prefix: '/billing-milestone-templates', permission: 'settings.billing_milestone_templates' },
  { prefix: '/checklist-templates', permission: 'settings.checklist_templates' },
  { prefix: '/system-logs', permission: 'settings.system_logs' },
  { prefix: '/city-records-sync-logs', permission: 'settings.city_sync' },
  { prefix: '/settings/team', permission: ['team.view', 'users.view'] },
  { prefix: '/settings/users', permission: ['team.view', 'users.view'] },
  { prefix: '/settings/qr-stickers', permission: 'properties.edit' },
  { prefix: '/settings', permission: 'settings.general' },
  // Ops discovery index — not for crew field roles (they only have dashboard.view).
  { prefix: '/directory', permission: ['settings.general', 'users.manage', 'review.view', 'jobs.dispatch'] },
]

export function permissionForPath(pathname: string): string | string[] | null {
  const match = ROUTE_PERMISSIONS.find((r) => pathname === r.prefix || pathname.startsWith(r.prefix + '/'))
  return match?.permission ?? null
}
